import { useState, useEffect } from "react";
import ConfigAPI from "../../services/ConfigAPI";

export default function PermissionSection() {
    const [permissions, setPermissions] = useState([]);
    const [resources, setResources] = useState([]);
    const [matrix, setMatrix] = useState([]);
    const [showAddModal, setShowAddModal] = useState(false);
    const [showResourceModal, setShowResourceModal] = useState(false);
    const [editingPermission, setEditingPermission] = useState(null);
    const [editingResource, setEditingResource] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    const [formData, setFormData] = useState({
        name: "",
        role: "admin",
        level: "",
        description: "",
        color: "#16a34a"
    });

    const [resourceFormData, setResourceFormData] = useState({
        key: "",
        name: "",
        description: ""
    });


    useEffect(() => {
        loadPermissions();
        loadMatrix();
    }, []);

    const loadPermissions = async () => {
        try {
            setLoading(true);
            const response = await ConfigAPI.getAllPermissions();

            if (response.success) {
                setPermissions(response.data);
            }
        } catch (err) {
            console.error("Erro ao carregar permissões:", err);
            setError("Erro ao carregar permissões");
        } finally {
            setLoading(false);
        }
    };

    const loadMatrix = async () => {
        try {
            const response = await ConfigAPI.getPermissionsMatrix();

            if (response.success) {
                setResources(response.data.resources);
                setMatrix(response.data.matrix);
            }
        } catch (err) {
            console.error("Erro ao carregar matriz:", err);
        }
    };

    const handleEdit = (permission) => {
        setEditingPermission(permission);
        setFormData({
            name: permission.name,
            role: permission.role || "",
            level: permission.level,
            description: permission.description,
            color: permission.color
        });
        setShowAddModal(true);
    };

    const handleEditResource = (resource) => {
        setEditingResource(resource);
        setResourceFormData({
            key: resource.key,
            name: resource.name,
            description: resource.description || ""
        });
        setShowResourceModal(true);
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Tem certeza que deseja excluir esta permissão?")) {
            return;
        }

        try {
            setLoading(true);
            const response = await ConfigAPI.deletePermission(id);

            if (response.success) {
                setSuccess("Permissão deletada com sucesso");
                setTimeout(() => setSuccess(null), 3000);
                await loadPermissions();
                await loadMatrix();
            }
        } catch (err) {
            console.error("Erro ao deletar permissão:", err);
            setError(err.response?.data?.message || "Erro ao deletar permissão");
            setTimeout(() => setError(null), 3000);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteResource = async (resourceKey) => {
        if (!window.confirm("Tem certeza que deseja excluir este recurso? Ele será removido de todas as permissões.")) {
            return;
        }

        try {
            setLoading(true);
            const response = await ConfigAPI.deleteResource(resourceKey);

            if (response.success) {
                setSuccess("Recurso deletado com sucesso");
                setTimeout(() => setSuccess(null), 3000);
                await loadMatrix();
            }
        } catch (err) {
            console.error("Erro ao deletar recurso:", err);
            setError(err.response?.data?.message || "Erro ao deletar recurso");
            setTimeout(() => setError(null), 3000);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async () => {
        if (!formData.name.trim() || formData.level === "" || !formData.description.trim()) {
            alert("Preencha todos os campos obrigatórios");
            return;
        }

        try {
            setLoading(true);
            let response;

            if (editingPermission) {
                response = await ConfigAPI.updatePermission(editingPermission._id, formData);
            } else {
                response = await ConfigAPI.createPermission(formData);
            }

            if (response.success) {
                setSuccess(editingPermission ? "Permissão atualizada com sucesso" : "Permissão criada com sucesso");
                setTimeout(() => setSuccess(null), 3000);
                setShowAddModal(false);
                resetForm();
                await loadPermissions();
                await loadMatrix();
            }
        } catch (err) {
            console.error("Erro ao salvar permissão:", err);
            setError(err.response?.data?.message || "Erro ao salvar permissão");
            setTimeout(() => setError(null), 3000);
        } finally {
            setLoading(false);
        }
    };

    const handleResourceSubmit = async () => {
        if (!resourceFormData.key.trim() || !resourceFormData.name.trim()) {
            alert("Preencha todos os campos obrigatórios");
            return;
        }

        try {
            setLoading(true);
            let response;

            if (editingResource) {
                response = await ConfigAPI.updateResource(editingResource.key, resourceFormData);
            } else {
                response = await ConfigAPI.createResource(resourceFormData);
            }

            if (response.success) {
                setSuccess(editingResource ? "Recurso atualizado com sucesso" : "Recurso criado com sucesso");
                setTimeout(() => setSuccess(null), 3000);
                setShowResourceModal(false);
                resetResourceForm();
                await loadMatrix();
            }
        } catch (err) {
            console.error("Erro ao salvar recurso:", err);
            setError(err.response?.data?.message || "Erro ao salvar recurso");
            setTimeout(() => setError(null), 3000);
        } finally {
            setLoading(false);
        }
    };

    const handleResourceToggle = async (resourceId, permissionType) => {
        try {
            const permission = permissions.find(p => p.name.toLowerCase() === permissionType);
            if (!permission) return;

            const resource = matrix.find(r => r.id === resourceId);
            if (!resource) return;

            const currentAccess = resource[permissionType];
            const newAccess = !currentAccess;

            await ConfigAPI.updateResourcePermission(permission._id, resourceId, newAccess);

            setMatrix(prev => prev.map(r => {
                if (r.id === resourceId) {
                    return { ...r, [permissionType]: newAccess };
                }
                return r;
            }));

            setSuccess("Permissão de recurso atualizada");
            setTimeout(() => setSuccess(null), 2000);
        } catch (err) {
            console.error("Erro ao atualizar permissão de recurso:", err);
            setError("Erro ao atualizar permissão de recurso");
            setTimeout(() => setError(null), 3000);
        }
    };

    const resetForm = () => {
        setFormData({
            name: "",
            role: "admin",
            level: "",
            description: "",
            color: "#16a34a"
        });
        setEditingPermission(null);
    };

    const resetResourceForm = () => {
        setResourceFormData({
            key: "",
            name: "",
            description: ""
        });
        setEditingResource(null);
    };

    function getContrastTextColor(hexColor) {
        if (!hexColor) return "#000";
        const hex = hexColor.replace("#", "");
        const r = parseInt(hex.substring(0, 2), 16);
        const g = parseInt(hex.substring(2, 4), 16);
        const b = parseInt(hex.substring(4, 6), 16);
        const luminance = (0.299 * r + 0.587 * g + 0.114 * b);
        return luminance > 186 ? "#000" : "#fff";
    }

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleResourceInputChange = (e) => {
        const { name, value } = e.target;
        setResourceFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    return (
        <div className="section-content">
            <h1>Gerenciamento de Permissões</h1>

            {success && (
                <div className="alert-success">
                    <span>✓ {success}</span>
                </div>
            )}

            {error && (
                <div style={{
                    background: '#fee2e2',
                    color: '#dc2626',
                    padding: '1rem',
                    borderRadius: '8px',
                    marginBottom: '1rem',
                    fontWeight: '500'
                }}>
                    {error}
                </div>
            )}

            <div className="config-card">
                <h3>Níveis de Permissão</h3>
                <p className="card-description">
                    Configure os diferentes níveis de acesso e suas permissões no sistema
                </p>

                {loading && permissions.length === 0 ? (
                    <div style={{ justifyContent: 'center', textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
                        Carregando...
                    </div>
                ) : (
                    <div className="permissions-grid">
                        {permissions.map((permission) => (
                            <div key={permission._id} className="permission-card"
                                style={{
                                    borderLeft: `5px solid ${permission?.color}`
                                }}>
                                <div className="permission-header">
                                    <div className="permission-title">
                                        <span className={`badge ${permission.color}`}
                                            style={{
                                                backgroundColor: permission?.color,
                                                color: getContrastTextColor(permission?.color)
                                            }}>
                                            {permission.name}
                                        </span>
                                        <span className="permission-level">Nível {permission.level}</span>
                                    </div>
                                    <div className="permission-actions">
                                        <button
                                            className="btn-icon"
                                            onClick={() => handleEdit(permission)}
                                            title="Editar permissão"
                                            disabled={loading}
                                        >
                                            ✏️
                                        </button>
                                        <button
                                            className="btn-icon btn-danger-icon"
                                            onClick={() => handleDelete(permission._id)}
                                            title="Excluir permissão"
                                            disabled={loading}
                                        >
                                            🗑️
                                        </button>
                                    </div>
                                </div>

                                <p className="permission-description">{permission.description}</p>

                                <div className="permission-stats">
                                    <div className="stat-item">
                                        <span className="stat-label">Usuários:</span>
                                        <span className="stat-value">{permission.users || 0}</span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                <button
                    className="btn-add"
                    onClick={() => {
                        resetForm();
                        setShowAddModal(true);
                    }}
                    disabled={loading}
                >
                    + Adicionar Permissão
                </button>
            </div>

            <div className="config-card">
                <div className="card-header">
                    <div>
                        <h3>Recursos do Sistema</h3>
                        <p className="card-description">
                            Gerencie os recursos disponíveis no sistema
                        </p>
                    </div>
                    <button
                        className="btn-add"
                        onClick={() => {
                            resetResourceForm();
                            setShowResourceModal(true);
                        }}
                        disabled={loading}
                        style={{ marginTop: 0 }}
                    >
                        + Adicionar Recurso
                    </button>
                </div>

                <div className="resources-list">

                    {matrix.length === 0 ? (
                        <tr>
                            <td colSpan={permissions.length + 1} style={{ textAlign: "center", padding: "16px" }}>
                                Nenhuma permissão ou recurso cadastrado
                            </td>
                        </tr>
                    ) : (
                        resources.map((resource) => (
                            <div key={resource.key} className="resource-item">
                                <div className="resource-info">
                                    <div>
                                        <h4>{resource.name}</h4>
                                        <span className="resource-key">{resource.key}</span>
                                    </div>
                                    {resource.description && (
                                        <p className="resource-description">{resource.description}</p>
                                    )}
                                </div>
                                <div className="resource-actions">
                                    <button
                                        className="btn-icon"
                                        onClick={() => handleEditResource(resource)}
                                        title="Editar recurso"
                                        disabled={loading}
                                    >
                                        ✏️
                                    </button>
                                    <button
                                        className="btn-icon btn-danger-icon"
                                        onClick={() => handleDeleteResource(resource.key)}
                                        title="Excluir recurso"
                                        disabled={loading}
                                    >
                                        🗑️
                                    </button>
                                </div>
                            </div>
                        )))}
                </div>
            </div>

            <div className="config-card">
                <h3>Matriz de Permissões</h3>
                <p className="card-description">
                    Visualize e edite quais recursos cada nível de permissão pode acessar
                </p>

                <div className="permissions-matrix">
                    <table>
                        <thead>
                            <tr>
                                <th>Recurso</th>
                                {permissions.map(permission => (
                                    <th key={permission._id}>{permission.name}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {matrix.length === 0 ? (
                                <tr>
                                    <td colSpan={permissions.length + 1} style={{ textAlign: "center", padding: "16px" }}>
                                        Nenhuma permissão ou recurso cadastrado
                                    </td>
                                </tr>
                            ) : (
                                matrix.map((resource) => (
                                    <tr key={resource.id}>
                                        <td>{resource.name}</td>

                                        {permissions.map(permission => {
                                            const permKey = permission.name.toLowerCase();
                                            const hasAccess = resource[permKey];

                                            return (
                                                <td key={permission._id}>
                                                    <span
                                                        className={hasAccess ? "check" : "cross"}
                                                        onClick={() =>
                                                            !loading && handleResourceToggle(resource.id, permKey)
                                                        }
                                                        style={{ cursor: loading ? 'not-allowed' : 'pointer' }}
                                                        title="Clique para alterar"
                                                    >
                                                        {hasAccess ? "✓" : "✗"}
                                                    </span>
                                                </td>
                                            );
                                        })}
                                    </tr>
                                ))
                            )}

                        </tbody>
                    </table>
                </div>
            </div>

            {/* Modal de Permissão */}
            {showAddModal && (
                <div className="modal-overlay" onClick={() => {
                    setShowAddModal(false);
                    resetForm();
                }}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <h2>{editingPermission ? "Editar Permissão" : "Nova Permissão"}</h2>

                        <div className="form-group">
                            <label>Nome da Permissão *</label>
                            <input
                                type="text"
                                name="name"
                                placeholder="Ex: Editor"
                                value={formData.name}
                                onChange={handleInputChange}
                                disabled={loading}
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label>Nível de Acesso *</label>
                            <input
                                type="number"
                                name="level"
                                min="0"
                                placeholder="0 = maior acesso"
                                value={formData.level}
                                onChange={handleInputChange}
                                disabled={loading}
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label>Tipo / Role (opcional)</label>
                            <select
                                name="role"
                                value={formData.role}
                                onChange={handleInputChange}
                                disabled={loading}
                            >
                                <option value="admin">Admin</option>
                                <option value="moderator">Moderador</option>
                                <option value="user">Usuário</option>
                                <option value="view">Visualizar</option>
                                <option value="suporte">Suporte</option>
                            </select>
                        </div>

                        <div className="form-group">
                            <label>Cor da Permissão</label>
                            <div className="color-picker">
                                <input
                                    type="color"
                                    name="color"
                                    value={formData.color}
                                    onChange={handleInputChange}
                                    disabled={loading}
                                />
                                <span className="color-preview-text">
                                    {formData.color}
                                </span>
                            </div>
                        </div>

                        <div className="form-group">
                            <label>Descrição *</label>
                            <textarea
                                name="description"
                                placeholder="Descreva as responsabilidades desta permissão"
                                rows="3"
                                value={formData.description}
                                onChange={handleInputChange}
                                disabled={loading}
                                required
                            ></textarea>
                        </div>

                        <div className="modal-actions">
                            <button
                                className="btn-secondary"
                                onClick={() => {
                                    setShowAddModal(false);
                                    resetForm();
                                }}
                                disabled={loading}
                            >
                                Cancelar
                            </button>
                            <button
                                className="btn-save"
                                onClick={handleSubmit}
                                disabled={loading}
                            >
                                {loading ? "Salvando..." : editingPermission ? "Atualizar" : "Criar"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal de Recurso */}
            {showResourceModal && (
                <div className="modal-overlay" onClick={() => {
                    setShowResourceModal(false);
                    resetResourceForm();
                }}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <h2>{editingResource ? "Editar Recurso" : "Novo Recurso"}</h2>

                        <div className="form-group">
                            <label>Chave do Recurso * (camelCase)</label>
                            <input
                                type="text"
                                name="key"
                                placeholder="Ex: manageUsers"
                                value={resourceFormData.key}
                                onChange={handleResourceInputChange}
                                disabled={loading || editingResource}
                                required
                                style={{ fontFamily: 'monospace' }}
                            />
                            <small style={{ color: '#6b7280', fontSize: '0.85rem' }}>
                                Use camelCase, sem espaços. Ex: dashboard, manageUsers, exportData
                            </small>
                        </div>

                        <div className="form-group">
                            <label>Nome de Exibição *</label>
                            <input
                                type="text"
                                name="name"
                                placeholder="Ex: Gerenciar Usuários"
                                value={resourceFormData.name}
                                onChange={handleResourceInputChange}
                                disabled={loading}
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label>Descrição</label>
                            <textarea
                                name="description"
                                placeholder="Descreva o que este recurso permite fazer"
                                rows="3"
                                value={resourceFormData.description}
                                onChange={handleResourceInputChange}
                                disabled={loading}
                            ></textarea>
                        </div>

                        <div className="modal-actions">
                            <button
                                className="btn-secondary"
                                onClick={() => {
                                    setShowResourceModal(false);
                                    resetResourceForm();
                                }}
                                disabled={loading}
                            >
                                Cancelar
                            </button>
                            <button
                                className="btn-save"
                                onClick={handleResourceSubmit}
                                disabled={loading}
                            >
                                {loading ? "Salvando..." : editingResource ? "Atualizar" : "Criar"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}